import { Controller } from 'stimulus'
import Autocomplete = google.maps.places.Autocomplete
import AutocompleteOptions = google.maps.places.AutocompleteOptions

export default class AddressAutocompleteController extends Controller {
  declare address1Target: HTMLInputElement
  declare cityTarget: HTMLInputElement
  declare stateTarget: HTMLInputElement
  declare zipTarget: HTMLInputElement

  static targets = ['address1', 'city', 'state', 'zip']

  // google places AutocompleteService used to get address suggestions
  // https://developers.google.com/maps/documentation/javascript/place-autocomplete
  autocomplete: Autocomplete | null = null
  autocompleteOptions: AutocompleteOptions = {
    componentRestrictions: { country: 'us' },
  }

  connect() {
    this.initializeAutocomplete()
  }

  disconnect() {
    this.teardownAutocomplete()
  }

  initializeAutocomplete() {
    this.autocomplete = new google.maps.places.Autocomplete(
      this.address1Target,
      this.autocompleteOptions,
    )

    // ensure autocomplete only returns addresses (i.e. not businesses)
    this.autocomplete.setTypes(['address'])

    // fill in address once place has been selected
    this.autocomplete.addListener(
      'place_changed',
      this.fillInAddress.bind(this),
    )
  }

  teardownAutocomplete() {
    this.autocomplete?.unbindAll()
    google.maps.event.clearInstanceListeners(this.address1Target)
    document.querySelectorAll('.pac-container').forEach((el) => el.remove())
  }

  fillInAddress() {
    const place = this.autocomplete?.getPlace()
    if (!place?.address_components) return

    for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
      const type = component.types[0]

      switch (type) {
        case 'street_number':
          this.address1Target.value = component.long_name
          break
        case 'route':
          this.address1Target.value += ' ' + component.long_name
          break
        case 'locality':
          this.cityTarget.value = component.long_name
          break
        case 'administrative_area_level_1':
          this.stateTarget.value = component.short_name
          break
        case 'postal_code':
          this.zipTarget.value = component.long_name
          break
      }
    }
  }

  /**
   * By default, the enter key will select an address from the google places and submit the form.
   * This method prevents the enter key from submitting the form when the google places dropdown is open.
   */
  maybePreventEnter(event: KeyboardEvent) {
    // "pac" can be read as "places auto complete"
    const pacContainer = document.querySelector<HTMLElement>('.pac-container')
    if (pacContainer?.style.display === 'none') return

    event.preventDefault()
  }
}
